import { Refresh } from "@mui/icons-material"
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tabs,
  Typography,
} from "@mui/material"
import { FC, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useGetSstv2ApiInfoQuery } from "src/features/partners/partnersApi"
import { useSearchParamsState } from "src/hooks/useSearchParamsState"
import SaLayout from "./SaLayout"
import { Sl2Tab } from "./Sl2Tab"
import SaSstv2ApiTester from "./SaSstv2ApiTester"
import SaSstv2Diagnostics from "./SaSstv2Diagnostics"
import SaSstv2Lockers from "./SaSstv2Lockers"
import SaSstv2Reservations from "./SaSstv2Reservations"
import SaSstv2SqsConfigs from "./SaSstv2SqsConfigs"
import SaSstv2DlqManagement from "./SaSstv2DlqManagement"
import { Sstv2EventFlowTab } from "./SaSstv2EventFlowTab"
import Sstv2TracesTab from "../features/sstv2-trace/Sstv2TracesTab"

const TabMap: Record<string, number> = {
  diagnostics: 0,
  lockers: 1,
  reservations: 2,
  sqsConfigs: 3,
  apiTester: 4,
  eventFlows: 5,
  liveTraces: 6,
  dlqViewer: 7,
}
const TabMapReverse: Record<number, string> = {
  0: "diagnostics",
  1: "lockers",
  2: "reservations",
  3: "sqsConfigs",
  4: "apiTester",
  5: "eventFlows",
  6: "liveTraces",
  7: "dlqViewer",
}

const cardSx = {
  bgcolor: "white",
  border: "1px solid",
  borderColor: "rgba(0, 0, 0, 0.08)",
  borderRadius: 2,
  boxShadow: "0 1px 2px rgba(35, 66, 133, 0.04)",
}

export const SaSstv2Page: FC = () => {
  const { t } = useTranslation()

  const qApiInfo = useGetSstv2ApiInfoQuery()
  const qApiInfoIsLoading = qApiInfo.isLoading || qApiInfo.isFetching

  const refresh = useCallback(() => {
    qApiInfo.refetch()
  }, [qApiInfo])

  const [tab, setTab] = useSearchParamsState("tab", TabMap.diagnostics + "")

  const handleTabChange = useCallback(
    (_event: React.ChangeEvent<{}>, newValue: number) => {
      setTab(newValue + "")
    },
    [setTab],
  )

  const apiInfo = qApiInfo.data?.data

  return (
    <SaLayout selectedMenuItemLabelKey="sstv2Api">
      <Box
        sx={{
          maxWidth: 1400,
          mx: "auto",
          px: { xs: 3, md: 5 },
          py: { xs: 4, md: 5 },
          width: "100%",
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          flexWrap="wrap"
          gap={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "primary.main",
              }}
            >
              {t("adminApp")}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                mt: 0.5,
                fontWeight: 700,
                letterSpacing: "-0.02em",
                lineHeight: 1.15,
              }}
            >
              SST V2 (Mobilus API)
            </Typography>
          </Box>
          {qApiInfoIsLoading ? (
            <CircularProgress size={22} />
          ) : (
            <IconButton
              onClick={refresh}
              size="small"
              sx={{
                bgcolor: "rgba(35, 66, 133, 0.06)",
                color: "primary.main",
                "&:hover": { bgcolor: "rgba(35, 66, 133, 0.12)" },
              }}
            >
              <Refresh fontSize="small" />
            </IconButton>
          )}
        </Stack>

        {/* Status / config card */}
        <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
          <Stack direction="row" spacing={1} alignItems="flex-end">
            <Typography variant="body2">{t("apiUrl")}</Typography>
            {qApiInfoIsLoading ? (
              <Skeleton variant="text" width={200} height={20} />
            ) : (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  variant="caption"
                  fontFamily="monospace"
                  sx={{
                    bgcolor: "rgba(0, 0, 0, 0.1)",
                    px: 1,
                  }}
                >
                  {apiInfo?.sstv2ApiUrl}
                </Typography>
                {apiInfo?.isOnline ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    divider={<Divider orientation="vertical" flexItem />}
                  >
                    <>
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: "green",
                        }}
                      />
                      <Typography variant="caption" color="green">
                        {t("online")}
                      </Typography>
                    </>
                    <Typography variant="caption">
                      {`${t("latency")}: ${apiInfo?.pingLatency}ms`}
                    </Typography>
                  </Stack>
                ) : (
                  <>
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "red",
                      }}
                    />
                    <Typography variant="caption" color="error">
                      {t("offline")}
                    </Typography>
                  </>
                )}
                {apiInfo?.tokenStatus && (
                  <Chip
                    label={
                      apiInfo.tokenError
                        ? `Token Error: ${apiInfo.tokenError}`
                        : "Token Valid"
                    }
                    size="small"
                    color={apiInfo.tokenError ? "error" : "success"}
                    variant="outlined"
                  />
                )}
              </Stack>
            )}
          </Stack>
        </Box>

        {/* Tabs rail */}
        <Box
          sx={{
            borderBottom: "1px solid",
            borderColor: "rgba(0, 0, 0, 0.08)",
            mb: 2.5,
          }}
        >
          <Tabs
            variant="scrollable"
            scrollButtons="auto"
            value={Number(tab)}
            onChange={handleTabChange}
            sx={{
              minHeight: 40,
              "& .MuiTab-root": { minHeight: 40, py: 1 },
            }}
          >
            <Sl2Tab label={t("diagnostics")} />
            <Sl2Tab label={t("lockers")} />
            <Sl2Tab label={t("reservations")} />
            <Sl2Tab label="SQS Configs" />
            <Sl2Tab label="API Tester" />
            <Sl2Tab label="Event Flows" />
            <Sl2Tab label="Live Traces" />
            <Sl2Tab label="DLQ Viewer" />
          </Tabs>
        </Box>

        {/* Tab content card */}
        <Box sx={{ ...cardSx, overflow: "hidden", p: { xs: 2, md: 2.5 } }}>
          {
            {
              diagnostics: (
                <SaSstv2Diagnostics
                  apiInfo={apiInfo}
                  isLoading={qApiInfoIsLoading}
                />
              ),
              lockers: <SaSstv2Lockers />,
              reservations: <SaSstv2Reservations />,
              sqsConfigs: <SaSstv2SqsConfigs />,
              apiTester: <SaSstv2ApiTester />,
              eventFlows: <Sstv2EventFlowTab />,
              liveTraces: <Sstv2TracesTab />,
              dlqViewer: <SaSstv2DlqManagement />,
            }[TabMapReverse[Number(tab)]]
          }
        </Box>
      </Box>
    </SaLayout>
  )
}

export default SaSstv2Page
